import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, Lock, User as UserIcon, LogOut, ArrowRight, Home, CheckCircle2, AlertCircle, Clock, Trash2, Inbox, Bell, Store, PackageCheck, Wrench } from "lucide-react";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import Seo from "@/seo/Seo";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel } from "@/lib/units";
import { api, floorLabel, gbp } from "@/lib/api";

// Google "G" mark (inline SVG — no emoji, no external asset).
function GoogleG({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.4l-6.5 5C9.6 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.7 35.9 44 30.4 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  );
}

export default function CustomerAccount() {
  const { user, loading, login, registerCustomer, logout, forgotPassword, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";

  const [mode, setMode] = useState("login"); // 'login' | 'register' | 'forgot'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resending, setResending] = useState(false);

  function signInWithGoogle() {
    sessionStorage.setItem("fm_auth_return", next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  async function handleForgot(e) {
    e.preventDefault();
    setBusy(true);
    const res = await forgotPassword(email.trim().toLowerCase());
    setBusy(false);
    if (res.ok) {
      setForgotSent(true);
    } else {
      toast.error(res.error || "Something went wrong");
    }
  }

  async function handleResend() {
    setResending(true);
    const res = await resendVerification();
    setResending(false);
    if (res.ok) toast.success("Verification email sent");
    else toast.error(res.error || "Couldn't send email");
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const res =
      mode === "login"
        ? await login(email.trim().toLowerCase(), password)
        : await registerCustomer({ email: email.trim().toLowerCase(), password, name: name.trim() });
    setBusy(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      // Send fitters to their portal when no explicit return path was requested.
      const dest = next === "/account" && res.user?.role === "fitter" ? "/fitter/dashboard" : next;
      navigate(dest, { replace: true });
    } else {
      toast.error(res.error || "Something went wrong");
    }
  }

  if (loading) {
    return (
      <>
        <PublicHeader />
        <div className="min-h-[60vh] grid place-items-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </>
    );
  }

  // ---------- Logged in: account summary ----------
  if (user) {
    return (
      <>
        <Seo title="My account — FloorMatch" description="Manage your FloorMatch account." path="/account" />
        <PublicHeader />
        <main className="min-h-screen bg-slate-50 py-10" data-testid="account-dashboard">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-4">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <span className="grid place-items-center w-14 h-14 rounded-full bg-[#0F3D2E] text-white font-display font-bold text-xl">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <h1 className="font-display font-bold text-2xl text-slate-900 truncate" data-testid="account-name">
                    {user.name || "Your account"}
                  </h1>
                  <p className="text-sm text-slate-500 truncate" data-testid="account-email">{user.email}</p>
                </div>
              </div>

              {user.role === "customer" && (
                <div className="mt-5 flex items-center gap-2 text-sm text-[#2A5A3B] bg-[#2A5A3B]/5 border border-[#2A5A3B]/15 rounded-xl px-4 py-3">
                  <CheckCircle2 className="w-4 h-4 shrink-0" /> You're signed in. Saved plans, stock alerts and requests will appear here.
                </div>
              )}

              {user.role === "customer" && user.email_verified === false && (
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="verify-banner">
                  <span className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
                    Please confirm your email to secure your account.
                  </span>
                  <button
                    onClick={handleResend}
                    disabled={resending}
                    className="sm:ml-auto inline-flex items-center gap-1.5 font-semibold text-amber-900 underline disabled:opacity-60"
                    data-testid="verify-resend"
                  >
                    {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Resend email
                  </button>
                </div>
              )}

              {(user.role === "retailer" || user.role === "admin") && (
                <Link
                  to={user.role === "admin" ? "/admin" : "/retailer"}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#E05915] hover:underline"
                  data-testid="account-dashboard-link"
                >
                  Go to your {user.role === "admin" ? "admin" : "retailer"} dashboard <ArrowRight className="w-4 h-4" />
                </Link>
              )}

              {user.role === "fitter" && (
                <Link
                  to="/fitter/dashboard"
                  className="mt-5 flex items-center justify-between gap-3 text-sm font-semibold text-white bg-[#0F3D2E] hover:bg-[#0c3325] rounded-xl px-4 py-3 transition-colors"
                  data-testid="account-fitter-dashboard-link"
                >
                  <span className="inline-flex items-center gap-2"><Wrench className="w-4 h-4" /> Go to your fitter dashboard</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}

              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <Link
                  to="/search"
                  className="inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold"
                  data-testid="account-find-flooring"
                >
                  Find flooring <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/whole-home"
                  className="inline-flex items-center justify-center gap-2 h-12 rounded-full border border-slate-300 bg-white font-semibold text-slate-700 hover:border-slate-400"
                  data-testid="account-whole-home"
                >
                  <Home className="w-4 h-4" /> Plan a whole home
                </Link>
              </div>

              {user.role === "customer" && <MyOrders />}
              {user.role === "customer" && <MyFitterRequests />}
              {user.role === "customer" && <MyRequests />}
              {user.role === "customer" && <MyAlerts />}

              <button
                onClick={async () => {
                  await logout();
                  toast.success("Signed out");
                  navigate("/");
                }}
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-800"
                data-testid="account-logout"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </div>
        </main>
      </>
    );
  }

  // ---------- Logged out: sign in / create account ----------
  return (
    <>
      <Seo title="Sign in — FloorMatch" description="Sign in or create a FloorMatch account to save your flooring plans, set stock alerts and reserve remnants." path="/account" />
      <PublicHeader />
      <main className="min-h-screen bg-slate-50 py-10" data-testid="account-auth">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h1 className="font-display font-bold text-2xl text-slate-900">
              {mode === "login" ? "Welcome back" : mode === "register" ? "Create your account" : "Reset your password"}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {mode === "login"
                ? "Sign in to save plans, set stock alerts and reserve flooring."
                : mode === "register"
                ? "Save your flooring plans, set stock alerts and reserve remnants."
                : "Enter your email and we'll send you a link to set a new password."}
            </p>

            {mode === "forgot" ? (
              forgotSent ? (
                <div className="mt-6 text-center" data-testid="forgot-sent">
                  <CheckCircle2 className="w-10 h-10 text-[#2A5A3B] mx-auto" />
                  <p className="text-sm text-slate-600 mt-3">
                    If an account exists for <b>{email}</b>, we've sent a reset link. Please check your inbox.
                  </p>
                  <button
                    onClick={() => { setMode("login"); setForgotSent(false); }}
                    className="mt-5 font-semibold text-[#E05915] hover:underline"
                    data-testid="forgot-back-login"
                  >
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-3 mt-5" data-testid="forgot-form">
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" /> Email
                    </span>
                    <input
                      type="email"
                      className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      data-testid="forgot-email-input"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full h-12 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70"
                    data-testid="forgot-submit"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Send reset link
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="w-full text-center text-sm text-slate-500 hover:text-slate-800"
                    data-testid="forgot-cancel"
                  >
                    Back to sign in
                  </button>
                </form>
              )
            ) : (
            <>
            <button
              onClick={signInWithGoogle}
              className="mt-5 w-full h-12 rounded-full border border-slate-300 bg-white font-semibold text-slate-700 inline-flex items-center justify-center gap-2.5 hover:bg-slate-50 transition-colors"
              data-testid="google-signin"
            >
              <GoogleG /> Continue with Google
            </button>

            <div className="flex items-center gap-3 my-5">
              <span className="h-px flex-1 bg-slate-200" />
              <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="account-form">
              {mode === "register" && (
                <label className="block">
                  <span className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                    <UserIcon className="w-3.5 h-3.5" /> Your name
                  </span>
                  <input
                    className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="name"
                    data-testid="account-name-input"
                  />
                </label>
              )}
              <label className="block">
                <span className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> Email
                </span>
                <input
                  type="email"
                  className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="account-email-input"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5" /> Password
                </span>
                <input
                  type="password"
                  className="w-full h-11 rounded-md border border-slate-300 px-3 focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={mode === "register" ? 8 : undefined}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  data-testid="account-password-input"
                />
                {mode === "register" && <span className="text-[11px] text-slate-400 mt-1 block">At least 8 characters.</span>}
              </label>

              {mode === "login" && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => { setForgotSent(false); setMode("forgot"); }}
                    className="text-xs font-semibold text-[#E05915] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid="account-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {mode === "login" ? "Sign in" : "Create account"}
              </button>
            </form>

            <p className="text-sm text-slate-500 text-center mt-5">
              {mode === "login" ? "New to FloorMatch?" : "Already have an account?"}{" "}
              <button
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="font-semibold text-[#E05915] hover:underline"
                data-testid="account-toggle-mode"
              >
                {mode === "login" ? "Create an account" : "Sign in"}
              </button>
            </p>
            </>
            )}
          </div>

          <p className="text-xs text-slate-400 text-center mt-4">
            Retailer or trade partner?{" "}
            <Link to="/retailer/login" className="underline hover:text-slate-600">Sign in here</Link>.
          </p>
        </div>
      </main>
    </>
  );
}

// ---------- My orders (combined multi-retailer) ----------
function MyOrders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    let on = true;
    api.get("/my/orders").then((r) => { if (on) setOrders(r.data.orders || []); }).catch(() => { if (on) setOrders([]); });
    return () => { on = false; };
  }, []);
  if (orders === null) return null;
  if (!orders.length) return null;
  const pill = (o) => {
    if (o.payment_status === "paid") return ["Paid", "bg-emerald-50 text-emerald-700"];
    if (o.payment_status === "partially_refunded") return ["Partly refunded", "bg-amber-50 text-amber-700"];
    if (o.payment_status === "refunded") return ["Refunded", "bg-slate-100 text-slate-600"];
    if (["expired", "cancelled"].includes(o.status)) return ["Cancelled", "bg-slate-100 text-slate-500"];
    return ["Awaiting payment", "bg-amber-50 text-amber-700"];
  };
  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-7" data-testid="my-orders">
      <h2 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2"><PackageCheck className="w-5 h-5 text-[#0F3D2E]" /> My orders</h2>
      <p className="text-sm text-slate-500 mt-1">Flooring you've paid for across one or more shops.</p>
      <div className="mt-4 space-y-4" data-testid="my-orders-list">
        {orders.map((o) => {
          const [label, cls] = pill(o);
          return (
            <div key={o.reference} className="border border-slate-200 rounded-xl p-4" data-testid={`my-order-${o.reference}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono font-semibold text-slate-800">{o.reference}</div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>
              </div>
              <div className="text-sm text-slate-500 mt-0.5">{gbp(o.total_gbp)} · {o.collections?.length} collection point{o.collections?.length !== 1 ? "s" : ""}</div>
              {o.paid && (
                <div className="mt-3 space-y-2">
                  {o.collections.map((c, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3">
                      <div className="font-semibold text-slate-800 flex items-center gap-1.5 text-sm"><Store className="w-4 h-4 text-emerald-700" />{c.shop?.name || `Collection point ${i + 1}`}</div>
                      {c.shop?.address && <div className="text-xs text-slate-500 mt-0.5">{c.shop.address}{c.shop.postcode ? `, ${c.shop.postcode}` : ""}{c.shop.phone ? ` · ${c.shop.phone}` : ""}</div>}
                      <div className="mt-1.5 text-xs text-slate-600">{c.items.map((it) => `${it.title} (${it.room_name})`).join(" · ")}</div>
                    </div>
                  ))}
                  <p className="text-[11px] text-slate-400">Bring order reference {o.reference} and photo ID to each shop to collect.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- My fitter requests (fitting / collection / delivery) ----------
const OPP_STATUS_STYLE = {
  offered: "bg-amber-50 text-amber-700",
  accepted: "bg-emerald-50 text-emerald-700",
  completed: "bg-[#0F3D2E]/10 text-[#0F3D2E]",
  cancelled: "bg-slate-100 text-slate-500",
};
const OPP_STATUS_LABEL = {
  offered: "Offered to fitters",
  accepted: "Fitter accepted",
  completed: "Completed",
  cancelled: "Cancelled",
};
const JOB_TYPE_LABEL = {
  collection: "Collection",
  fitting: "Fitting",
  collection_fitting: "Collection & fitting",
  delivery: "Delivery",
  measuring: "Measuring service",
};

function MyFitterRequests() {
  const { unit } = useUnit();
  const [opps, setOpps] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const r = await api.get("/opportunities/mine");
      setOpps(r.data.opportunities || []);
    } catch (e) {
      setOpps([]);
    }
  }
  useEffect(() => { load(); }, []);

  async function cancel(id) {
    setBusyId(id);
    try {
      await api.patch(`/opportunities/${id}/cancel`);
      setOpps((prev) => (prev || []).map((o) => (o.id === id ? { ...o, status: "cancelled" } : o)));
      toast.success("Fitter request cancelled");
    } catch (e) {
      toast.error("Couldn't cancel request");
    } finally {
      setBusyId(null);
    }
  }

  if (opps === null) {
    return (
      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-7" data-testid="my-fitter-requests">
        <div className="py-6 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      </div>
    );
  }
  if (!opps.length) return null; // nothing yet — keep the account tidy

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-7" data-testid="my-fitter-requests">
      <div className="flex items-center gap-2">
        <Wrench className="w-5 h-5 text-[#0F3D2E]" />
        <h2 className="font-display font-bold text-xl text-slate-900">My fitter requests</h2>
      </div>
      <p className="text-sm text-slate-500 mt-1">Fitting, collection and delivery jobs you've sent to approved local fitters. Your identity stays private until a fitter is confirmed.</p>

      <div className="mt-4 space-y-3" data-testid="my-fitter-requests-list">
        {opps.map((o) => (
          <div key={o.id} className="border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`fitter-request-row-${o.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900">{o.title || "Flooring job"}</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${OPP_STATUS_STYLE[o.status] || "bg-slate-100 text-slate-500"}`} data-testid={`fitter-request-status-${o.id}`}>
                  {OPP_STATUS_LABEL[o.status] || o.status}
                </span>
              </div>
              <div className="text-sm text-slate-600 mt-1">
                {JOB_TYPE_LABEL[o.job_type] || o.job_type}
                {o.flooring_type ? ` · ${floorLabel(o.flooring_type)}` : ""}
                {o.rooms?.length ? ` · ${o.rooms.length} room${o.rooms.length !== 1 ? "s" : ""}` : ""}
              </div>
              {o.rooms?.length ? (
                <div className="text-xs text-slate-400 mt-0.5 truncate">
                  {o.rooms.map((r) => `${r.name || "Room"} ${dimPairLabel(r.width_m, r.length_m, unit)}`).join(" · ")}
                </div>
              ) : null}
              <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {o.reference_code}{o.postcode ? ` · ${o.postcode}` : ""}
              </div>
            </div>
            {["offered", "accepted"].includes(o.status) && (
              <button
                onClick={() => cancel(o.id)}
                disabled={busyId === o.id}
                className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-red-600 disabled:opacity-60"
                data-testid={`cancel-fitter-request-${o.id}`}
              >
                {busyId === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Cancel
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------- My flooring requests ----------
// ---------- My flooring requests ----------
function MyRequests() {
  const { unit } = useUnit();
  const [requests, setRequests] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const r = await api.get("/requests/mine");
      setRequests(r.data.requests || []);
    } catch (e) {
      setRequests([]);
    }
  }

  useEffect(() => { load(); }, []);

  async function withdraw(id) {
    setBusyId(id);
    try {
      await api.patch(`/requests/${id}`);
      setRequests((prev) => (prev || []).map((req) => (req.id === id ? { ...req, status: "cancelled" } : req)));
      toast.success("Request withdrawn");
    } catch (e) {
      toast.error("Couldn't withdraw request");
    } finally {
      setBusyId(null);
    }
  }

  const statusStyle = {
    active: "bg-[#2A5A3B]/10 text-[#2A5A3B]",
    cancelled: "bg-slate-100 text-slate-500",
    fulfilled: "bg-[#E05915]/10 text-[#E05915]",
    paused: "bg-amber-100 text-amber-700",
  };

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-7" data-testid="my-requests">
      <div className="flex items-center gap-2">
        <Inbox className="w-5 h-5 text-[#0F3D2E]" />
        <h2 className="font-display font-bold text-xl text-slate-900">My flooring requests</h2>
      </div>
      <p className="text-sm text-slate-500 mt-1">Requests you've sent to nearby retailers. Your identity stays private — they only see the room size and type.</p>

      {requests === null ? (
        <div className="py-8 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : requests.length === 0 ? (
        <div className="mt-4 text-center py-8 text-slate-500 text-sm" data-testid="my-requests-empty">
          <p>You haven't sent any requests yet.</p>
          <p className="mt-1">Can't find the right flooring? Plan your rooms and ask nearby retailers.</p>
          <Link to="/whole-home" className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold text-sm" data-testid="my-requests-cta">
            Plan your flooring <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="my-requests-list">
          {requests.map((req) => (
            <div key={req.id} className="border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`request-row-${req.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">{req.room_name || "Room"}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusStyle[req.status] || "bg-slate-100 text-slate-500"}`} data-testid={`request-status-${req.id}`}>
                    {req.status}
                  </span>
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  {floorLabel(req.flooring_type)}{req.colour ? ` · ${req.colour}` : ""} · {dimPairLabel(req.room_width_m, req.room_length_m, unit)} ({req.room_area_m2} m²)
                </div>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {req.postcode}{req.expires_at ? ` · expires ${new Date(req.expires_at).toLocaleDateString("en-GB")}` : ""}
                </div>
              </div>
              {req.status === "active" && (
                <button
                  onClick={() => withdraw(req.id)}
                  disabled={busyId === req.id}
                  className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-red-600 disabled:opacity-60"
                  data-testid={`withdraw-request-${req.id}`}
                >
                  {busyId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Withdraw
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- My stock alerts (account-gated) ----------
function MyAlerts() {
  const { unit } = useUnit();
  const [alerts, setAlerts] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const r = await api.get("/alerts/mine");
      setAlerts(r.data.alerts || []);
    } catch (e) {
      setAlerts([]);
    }
  }
  useEffect(() => { load(); }, []);

  async function unsubscribe(id) {
    setBusyId(id);
    try {
      await api.post(`/alerts/mine/${id}/unsubscribe`);
      setAlerts((prev) => (prev || []).filter((a) => a.id !== id));
      toast.success("Alert removed");
    } catch (e) {
      toast.error("Couldn't remove alert");
    } finally {
      setBusyId(null);
    }
  }

  function describe(a) {
    const bits = [];
    if (a.room_width_m && a.room_length_m) bits.push(dimPairLabel(a.room_width_m, a.room_length_m, unit));
    if (a.flooring_type) bits.push(floorLabel(a.flooring_type));
    if (a.max_price) bits.push(`under £${a.max_price}`);
    if (a.postcode) bits.push(`near ${a.postcode} (within ${Math.round((a.radius_km || 25) / 1.60934)} miles)`);
    return bits.length ? bits.join(" · ") : "Any new FloorMatch listing";
  }

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-7" data-testid="my-alerts">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-[#0F3D2E]" />
        <h2 className="font-display font-bold text-xl text-slate-900">My stock alerts</h2>
      </div>
      <p className="text-sm text-slate-500 mt-1">We send a daily email each morning with any new matching surplus flooring. Manage your alerts here.</p>

      {alerts === null ? (
        <div className="py-8 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : alerts.length === 0 ? (
        <div className="mt-4 text-center py-8 text-slate-500 text-sm" data-testid="my-alerts-empty">
          <p>You don't have any stock alerts yet.</p>
          <p className="mt-1">Run a search and tap “Alert me when this lands” to get notified.</p>
          <Link to="/search" className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold text-sm" data-testid="my-alerts-cta">
            Find flooring <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="my-alerts-list">
          {alerts.map((a) => (
            <div key={a.id} className="border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-3" data-testid={`alert-row-${a.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900">{describe(a)}</div>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Added {new Date(a.created_at).toLocaleDateString("en-GB")}
                </div>
              </div>
              <button
                onClick={() => unsubscribe(a.id)}
                disabled={busyId === a.id}
                className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-red-600 disabled:opacity-60"
                data-testid={`remove-alert-${a.id}`}
              >
                {busyId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
